import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Check, X } from 'lucide-react';
import React from 'react';

import { cn } from '@/lib/utils';

import { DialogOverlay, DialogPortal } from './dialog';

export interface StepDialogStep {
  id: string;
  label: string;
}

export interface StepDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  steps: StepDialogStep[];
  currentStep: number;
  completedSteps: Set<number>;
  onStepClick?: (stepIndex: number) => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  preventClose?: boolean;
}

const StepIndicator: React.FC<{
  index: number;
  isCompleted: boolean;
  isCurrent: boolean;
}> = ({ index, isCompleted, isCurrent }) => {
  if (isCompleted) {
    return (
      <span className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full bg-green-100 text-green-600">
        <Check className="h-3.5 w-3.5" />
      </span>
    );
  }
  if (isCurrent) {
    return (
      <span className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full bg-violet-600 text-[11px] font-medium text-white">
        {index + 1}
      </span>
    );
  }
  return (
    <span className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full border border-zinc-300 text-[11px] font-medium text-zinc-500">
      {index + 1}
    </span>
  );
};

export const StepDialog: React.FC<StepDialogProps> = ({
  open,
  onOpenChange,
  title,
  description,
  steps,
  currentStep,
  completedSteps,
  onStepClick,
  children,
  footer,
  preventClose,
}) => {
  const handleOpenChange = (next: boolean) => {
    if (preventClose && !next) return;
    onOpenChange(next);
  };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={handleOpenChange}>
      <DialogPortal>
        <DialogOverlay />
        <DialogPrimitive.Content
          className={cn(
            'fixed left-[50%] top-[50%] z-50 w-full translate-x-[-50%] translate-y-[-50%] border bg-background shadow-lg duration-200',
            'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]',
            'sm:max-w-3xl sm:rounded-lg p-0 overflow-hidden',
          )}
        >
          <div className="flex flex-row">
            {/* Left sidebar */}
            <div className="w-[200px] shrink-0 border-r border-zinc-100 bg-zinc-50/50 p-5">
              <DialogPrimitive.Title className="text-base font-semibold leading-tight tracking-tight text-zinc-900">
                {title}
              </DialogPrimitive.Title>
              <nav className="mt-5" aria-label="Wizard steps">
                <ol className="space-y-0">
                  {steps.map((step, index) => {
                    const isCompleted = completedSteps.has(index);
                    const isCurrent = index === currentStep;
                    const isFuture = !isCompleted && !isCurrent;
                    const isClickable = isCompleted && !preventClose;

                    return (
                      <li key={step.id} className="relative">
                        {/* Connecting line */}
                        {index < steps.length - 1 && (
                          <div
                            className={cn(
                              'absolute left-[11px] top-[28px] h-[20px] w-[2px]',
                              completedSteps.has(index)
                                ? 'bg-green-400'
                                : 'bg-zinc-200',
                            )}
                          />
                        )}
                        <button
                          type="button"
                          className={cn(
                            'flex w-full items-center gap-3 rounded-md px-1 py-2 text-left text-sm transition-colors',
                            isClickable &&
                              'cursor-pointer hover:bg-zinc-100',
                            isFuture && 'cursor-default',
                            isCurrent && 'cursor-default',
                          )}
                          onClick={() => {
                            if (isClickable && onStepClick) {
                              onStepClick(index);
                            }
                          }}
                          disabled={isFuture || preventClose}
                          aria-current={isCurrent ? 'step' : undefined}
                        >
                          <StepIndicator
                            index={index}
                            isCompleted={isCompleted}
                            isCurrent={isCurrent}
                          />
                          <span
                            className={cn(
                              'font-medium',
                              isCurrent && 'text-zinc-900',
                              isCompleted && 'text-zinc-700',
                              isFuture && 'text-zinc-500',
                            )}
                          >
                            {step.label}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ol>
              </nav>
            </div>

            {/* Right content */}
            <div className="flex flex-1 flex-col min-h-[420px]">
              {description && (
                <DialogPrimitive.Description className="px-6 pt-5 pb-2 text-sm text-muted-foreground">
                  {description}
                </DialogPrimitive.Description>
              )}
              <div className="flex-1 overflow-y-auto px-6 py-3">{children}</div>
              {footer && (
                <div className="border-t border-zinc-100 px-6 py-4">
                  {footer}
                </div>
              )}
            </div>
          </div>

          {/* Close button */}
          {!preventClose && (
            <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          )}
        </DialogPrimitive.Content>
      </DialogPortal>
    </DialogPrimitive.Root>
  );
};
